/**
 * Guest Analytics Service
 * Tracks anonymous visitor behaviour and aggregates it in Firestore.
 * All data is collectively anonymised — no individual tracking.
 */

import { db } from '@/lib/firebase';
import { doc, getDoc, setDoc, updateDoc, increment } from 'firebase/firestore';

// Empty nested-map skeleton — written once when the aggregate doc is first created.
// updateDoc() requires the document to exist; we create it with this structure.
const EMPTY_AGG = {
  totalPageViews: 0, totalSessions: 0,
  browsers: {}, browserVersions: {}, devices: {}, os: {},
  pages: {}, hourOfDay: {}, dayOfWeek: {}, screenSizes: {},
  referrers: {}, languages: {}, timezones: {}, colorSchemes: {}, pixelRatios: {},
  mobileModels: {}, clicks: {},
  user: {
    browsers: {}, browserVersions: {}, devices: {}, os: {},
    pages: {}, hourOfDay: {}, dayOfWeek: {}, screenSizes: {},
    referrers: {}, languages: {}, timezones: {}, colorSchemes: {}, pixelRatios: {},
    mobileModels: {}, clicks: {},
  },
};

const AGG_DOC = 'guestAnalytics/aggregate';

// ── Browser / device detection ────────────────────────────────────────────────

function detectBrowser(): string {
  if (typeof navigator === 'undefined') return 'Unknown';
  const ua = navigator.userAgent;
  if (ua.includes('Edg/')) return 'Edge';
  if (ua.includes('OPR/') || ua.includes('Opera')) return 'Opera';
  if (ua.includes('Brave')) return 'Brave';
  if (ua.includes('Chrome/') && !ua.includes('Chromium')) return 'Chrome';
  if (ua.includes('Chromium/')) return 'Chromium';
  if (ua.includes('Firefox/')) return 'Firefox';
  if (ua.includes('Safari/') && !ua.includes('Chrome')) return 'Safari';
  return 'Other';
}

function detectBrowserVersion(): string {
  if (typeof navigator === 'undefined') return 'Unknown';
  const ua = navigator.userAgent;
  const b = detectBrowser();
  let m: RegExpMatchArray | null = null;
  if (b === 'Chrome')   m = ua.match(/Chrome\/(\d+)/);
  else if (b === 'Firefox') m = ua.match(/Firefox\/(\d+)/);
  else if (b === 'Safari')  m = ua.match(/Version\/(\d+)/);
  else if (b === 'Edge')    m = ua.match(/Edg\/(\d+)/);
  else if (b === 'Opera')   m = ua.match(/OPR\/(\d+)/);
  else if (b === 'Chromium') m = ua.match(/Chromium\/(\d+)/);
  return m ? `${b} ${m[1]}` : b;
}

function detectDevice(): 'mobile' | 'tablet' | 'desktop' {
  if (typeof navigator === 'undefined') return 'desktop';
  const ua = navigator.userAgent;
  if (/iPad/i.test(ua) || (window.innerWidth >= 768 && /Tablet|Android/i.test(ua))) return 'tablet';
  if (/Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Windows Phone/i.test(ua)) return 'mobile';
  return 'desktop';
}

function detectOS(): string {
  if (typeof navigator === 'undefined') return 'Unknown';
  const ua = navigator.userAgent;
  if (/Windows NT 10/i.test(ua)) return 'Windows 10/11';
  if (/Windows NT/i.test(ua)) return 'Windows';
  if (/Mac OS X/i.test(ua) && !/iPhone|iPad/i.test(ua)) return 'macOS';
  if (/iPhone/i.test(ua)) return 'iOS';
  if (/iPad/i.test(ua)) return 'iPadOS';
  if (/Android/i.test(ua)) return 'Android';
  if (/Linux/i.test(ua)) return 'Linux';
  return 'Other';
}

function detectOSVersion(): string {
  if (typeof navigator === 'undefined') return '';
  const ua = navigator.userAgent;
  const os = detectOS();
  let m: RegExpMatchArray | null = null;
  if (os === 'macOS') {
    m = ua.match(/Mac OS X ([\d_]+)/);
    return m ? `macOS ${m[1].replace(/_/g, '.')}` : 'macOS';
  }
  if (os === 'iOS') {
    m = ua.match(/OS ([\d_]+) like Mac/);
    return m ? `iOS ${m[1].replace(/_/g, '.')}` : 'iOS';
  }
  if (os === 'Android') {
    m = ua.match(/Android ([\d.]+)/);
    return m ? `Android ${m[1]}` : 'Android';
  }
  if (os === 'Windows 10/11') {
    m = ua.match(/Windows NT ([\d.]+)/);
    const build = m ? parseFloat(m[1]) : 0;
    return build >= 10 ? 'Windows 10/11' : 'Windows';
  }
  return os;
}

/** Returns a human-readable mobile device model derived from UA + screen dimensions. */
function detectMobileModel(): string {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') return 'unknown';
  const ua = navigator.userAgent;
  const device = detectDevice();
  if (device === 'desktop') return 'desktop';

  // iPhone — identified by screen size × DPR combo (most reliable cross-browser method)
  if (/iPhone/i.test(ua)) {
    const w = Math.min(screen.width, screen.height);
    const h = Math.max(screen.width, screen.height);
    const dpr = window.devicePixelRatio;
    if (w === 440 && h === 956 && dpr === 3) return 'iPhone 16 Pro Max';
    if (w === 402 && h === 874 && dpr === 3) return 'iPhone 16 Pro';
    if (w === 430 && h === 932 && dpr === 3) return 'iPhone 15 Plus / 14 Pro Max';
    if (w === 393 && h === 852 && dpr === 3) return 'iPhone 15 / 14 Pro';
    if (w === 390 && h === 844 && dpr === 3) return 'iPhone 14 / 13 / 12';
    if (w === 428 && h === 926 && dpr === 3) return 'iPhone 13 Pro Max / 12 Pro Max';
    if (w === 390 && h === 844 && dpr === 3) return 'iPhone 13 Pro / 12 Pro';
    if (w === 375 && h === 812 && dpr === 3) return 'iPhone 13 Mini / 12 Mini / X / 11 Pro';
    if (w === 414 && h === 896 && dpr === 3) return 'iPhone 11 Pro Max / XS Max';
    if (w === 414 && h === 896 && dpr === 2) return 'iPhone 11 / XR';
    if (w === 375 && h === 667 && dpr === 2) return 'iPhone SE / 8 / 7';
    if (w === 414 && h === 736 && dpr === 3) return 'iPhone 8 Plus / 7 Plus';
    if (w === 320 && h === 568 && dpr === 2) return 'iPhone SE (1st gen) / 5';
    return 'iPhone (other)';
  }

  // iPad
  if (/iPad/i.test(ua)) {
    const w = Math.min(screen.width, screen.height);
    if (w >= 1024) return 'iPad Pro 12.9"';
    if (w >= 834) return 'iPad Pro 11" / iPad Air';
    if (w >= 768) return 'iPad / iPad Mini';
    return 'iPad (other)';
  }

  // Android — extract model name from UA
  const m = ua.match(/Android[\s/][\d.]+;\s*([^)]+)/i);
  if (m) {
    const raw = m[1].trim().split(';')[0].trim().replace(/\s+Build.*/i, '').trim();
    // Android 13+ Chrome froze the device-model token to the literal "K" as a
    // privacy/User-Agent-Reduction measure — so most modern Android Chrome
    // visitors report model "K". Label it clearly instead of a cryptic "K".
    if (!raw || raw === 'K' || /^wv$/i.test(raw)) return 'Android phone (model hidden)';
    return raw;
  }

  return device === 'tablet' ? 'Tablet' : 'Mobile (other)';
}

function detectColorScheme(): 'dark' | 'light' | 'no-preference' {
  if (typeof window === 'undefined') return 'no-preference';
  if (window.matchMedia('(prefers-color-scheme: dark)').matches) return 'dark';
  if (window.matchMedia('(prefers-color-scheme: light)').matches) return 'light';
  return 'no-preference';
}

function detectReducedMotion(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function detectOrientation(): string {
  if (typeof screen !== 'undefined' && screen.orientation) return screen.orientation.type;
  if (typeof window !== 'undefined') return window.innerWidth > window.innerHeight ? 'landscape-primary' : 'portrait-primary';
  return 'unknown';
}

function detectMediaCodecs(): { mp3: boolean; aac: boolean; ogg: boolean; webm: boolean; flac: boolean; wav: boolean; hevc: boolean; av1: boolean } {
  if (typeof document === 'undefined') return { mp3: false, aac: false, ogg: false, webm: false, flac: false, wav: false, hevc: false, av1: false };
  const a = document.createElement('audio');
  const v = document.createElement('video');
  const canPlay = (el: HTMLMediaElement, type: string) => el.canPlayType(type) !== '';
  return {
    mp3:  canPlay(a, 'audio/mpeg'),
    aac:  canPlay(a, 'audio/aac'),
    ogg:  canPlay(a, 'audio/ogg; codecs="vorbis"'),
    webm: canPlay(a, 'audio/webm; codecs="opus"'),
    flac: canPlay(a, 'audio/flac'),
    wav:  canPlay(a, 'audio/wav'),
    hevc: canPlay(v, 'video/mp4; codecs="hev1.1.6.L93.B0"'),
    av1:  canPlay(v, 'video/webm; codecs="av01.0.05M.08"'),
  };
}

function getCanvasFingerprint(): string | null {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 200; canvas.height = 50;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillStyle = '#f60';
    ctx.fillRect(125, 1, 62, 20);
    ctx.fillStyle = '#069';
    ctx.fillText('BB Visitor 🎵', 2, 15);
    ctx.fillStyle = 'rgba(102,204,0,0.7)';
    ctx.fillText('BB Visitor 🎵', 4, 17);
    // Simple hash of the data URL
    const data = canvas.toDataURL();
    let hash = 0;
    for (let i = 0; i < data.length; i++) {
      hash = ((hash << 5) - hash) + data.charCodeAt(i);
      hash |= 0;
    }
    return Math.abs(hash).toString(16).toUpperCase().padStart(8, '0');
  } catch {
    return null;
  }
}

function getAudioContextFingerprint(): { sampleRate: number; channelCount: number; state: string } | null {
  try {
    const ctx = new AudioContext();
    const info = {
      sampleRate: ctx.sampleRate,
      channelCount: ctx.destination.maxChannelCount,
      state: ctx.state,
    };
    ctx.close();
    return info;
  } catch {
    return null;
  }
}

function sanitiseReferrer(ref: string): string {
  if (!ref) return 'Direct';
  try {
    const url = new URL(ref);
    const host = url.hostname.replace(/^www\./, '');
    // Only keep the domain, no paths (privacy)
    return host || 'Direct';
  } catch {
    return 'Other';
  }
}

// ── Types ─────────────────────────────────────────────────────────────────────

export interface GuestAnalyticsAggregate {
  // ── Guest (unauthenticated) counters ──
  totalPageViews: number;
  totalSessions: number;
  pages: Record<string, number>;
  browsers: Record<string, number>;
  browserVersions: Record<string, number>;
  devices: Record<string, number>;
  os: Record<string, number>;
  hourOfDay: Record<string, number>;
  dayOfWeek: Record<string, number>;
  screenSizes: Record<string, number>;
  referrers: Record<string, number>;
  languages: Record<string, number>;
  timezones: Record<string, number>;
  colorSchemes: Record<string, number>;
  pixelRatios: Record<string, number>;
  mobileModels: Record<string, number>;
  clicks: Record<string, number>;

  // ── Logged-in user counters (prefix "user") ──
  userTotalPageViews?: number;
  userTotalSessions?: number;
  user?: {
    pages?: Record<string, number>;
    browsers?: Record<string, number>;
    devices?: Record<string, number>;
    os?: Record<string, number>;
    hourOfDay?: Record<string, number>;
    languages?: Record<string, number>;
    timezones?: Record<string, number>;
    colorSchemes?: Record<string, number>;
    referrers?: Record<string, number>;
    screenSizes?: Record<string, number>;
    pixelRatios?: Record<string, number>;
    browserVersions?: Record<string, number>;
    dayOfWeek?: Record<string, number>;
    mobileModels?: Record<string, number>;
    clicks?: Record<string, number>;
  };

  updatedAt?: string;
}

export interface MediaCodecSupport {
  mp3: boolean; aac: boolean; ogg: boolean; webm: boolean;
  flac: boolean; wav: boolean; hevc: boolean; av1: boolean;
}

export interface AudioContextInfo {
  sampleRate: number;
  channelCount: number;
  state: string;
}

export interface BrowserProfile {
  browser: string;
  browserVersion: string;
  os: string;
  osVersion: string;
  device: string;
  screenW: number;
  screenH: number;
  viewportW: number;
  viewportH: number;
  pixelRatio: number;
  colorDepth: number;
  timezone: string;
  language: string;
  languages: string[];
  cookiesEnabled: boolean;
  doNotTrack: boolean | null;
  hardwareConcurrency: number;
  deviceMemoryGb: number | null;
  touchPoints: number;
  onLine: boolean;
  connectionType: string | null;
  downlinkMbps: number | null;
  rttMs: number | null;
  localStorageAvailable: boolean;
  sessionStorageAvailable: boolean;
  indexedDBAvailable: boolean;
  serviceWorkerAvailable: boolean;
  webAssemblyAvailable: boolean;
  geolocationAvailable: boolean;
  webShareAvailable: boolean;
  webGLRenderer: string | null;
  webGLVendor: string | null;
  batteryLevel: number | null;
  batteryCharging: boolean | null;
  referrer: string;
  plugins: number;
  // New fields
  colorScheme: 'dark' | 'light' | 'no-preference';
  reducedMotion: boolean;
  highContrast: boolean;
  orientation: string;
  mediaCodecs: MediaCodecSupport;
  canvasFingerprint: string | null;
  audioContext: AudioContextInfo | null;
}

// ── Shared dimension collector (returns field updates for setDoc) ─────────────

function buildDimensionUpdates(pathname: string, prefix = ''): Record<string, unknown> {
  const browser  = detectBrowser();
  const bVersion = detectBrowserVersion();
  const device   = detectDevice();
  const os       = detectOS();
  const hour     = new Date().getHours().toString();
  const day      = new Date().getDay().toString();
  const pageName = pathname.split('/').filter(Boolean)[0] || 'home';
  const w        = window.innerWidth;
  const sizeBucket =
    w < 480  ? 'xs_480'  :
    w < 768  ? 'sm_480_767' :
    w < 1024 ? 'md_768_1023' :
    w < 1440 ? 'lg_1024_1439' : 'xl_1440plus';
  const referrer  = sanitiseReferrer(document.referrer);
  // Use the site locale (NEXT_LOCALE cookie, set from IP geo or user preference) as the
  // primary language signal. This correctly records 'sv' for a Swedish visitor even if
  // their browser is installed in English. Falls back to navigator.language if no cookie.
  const lang = (() => {
    const match = document.cookie.match(/(?:^|;\s*)NEXT_LOCALE=([^;]+)/);
    if (match?.[1]) return match[1];
    return navigator.language?.split('-')[0] || 'unknown';
  })();
  const tz        = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const scheme    = detectColorScheme();
  const dpr       = window.devicePixelRatio;
  const dprBucket = dpr >= 3 ? '3x' : dpr >= 2 ? '2x' : '1x';
  const p = prefix ? `${prefix}.` : '';

  const mobileModel = detectMobileModel();

  return {
    [`${p}pages.${pageName}`]: increment(1),
    [`${p}browsers.${browser}`]: increment(1),
    [`${p}browserVersions.${bVersion.replace(/[^a-z0-9]/gi, '_')}`]: increment(1),
    [`${p}devices.${device}`]: increment(1),
    [`${p}os.${os.replace(/[^a-z0-9]/gi, '_')}`]: increment(1),
    [`${p}hourOfDay.${hour}`]: increment(1),
    [`${p}dayOfWeek.${day}`]: increment(1),
    [`${p}screenSizes.${sizeBucket}`]: increment(1),
    [`${p}referrers.${referrer.replace(/[^a-z0-9]/gi, '_')}`]: increment(1),
    [`${p}languages.${lang}`]: increment(1),
    [`${p}timezones.${tz.replace(/[^a-z0-9]/gi, '_')}`]: increment(1),
    [`${p}colorSchemes.${scheme}`]: increment(1),
    [`${p}pixelRatios.${dprBucket}`]: increment(1),
    ...(mobileModel !== 'desktop' ? { [`${p}mobileModels.${mobileModel.replace(/[^a-z0-9]/gi, '_')}`]: increment(1) } : {}),
  };
}

// ── Ensure the aggregate document exists before calling updateDoc ─────────────
// updateDoc() throws if the document doesn't exist; we create it once.
async function ensureAggregateDoc(): Promise<void> {
  const ref = doc(db, 'guestAnalytics', 'aggregate');
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    await setDoc(ref, EMPTY_AGG);
  }
}

// ── Track a guest (unauthenticated) page view ─────────────────────────────────
// IMPORTANT: uses updateDoc() not setDoc()+merge so that dot-notation keys
// ('browsers.Chrome', 'hourOfDay.12', etc.) are correctly written as NESTED
// field paths — setDoc+merge in the v9 modular SDK treats them as literal
// top-level field names, which breaks all the breakdown charts.

export async function trackGuestPageView(pathname: string): Promise<void> {
  if (typeof window === 'undefined') return;
  try {
    const sessionKey = 'bb_session_tracked';
    const isNewSession = !sessionStorage.getItem(sessionKey);
    if (isNewSession) sessionStorage.setItem(sessionKey, '1');

    const ref = doc(db, 'guestAnalytics', 'aggregate');
    const updates = {
      totalPageViews: increment(1),
      ...(isNewSession ? { totalSessions: increment(1) } : {}),
      ...buildDimensionUpdates(pathname),
      updatedAt: new Date().toISOString(),
    };

    try {
      await updateDoc(ref, updates);
    } catch {
      // Document may not exist yet — create it, then retry
      await ensureAggregateDoc();
      await updateDoc(ref, updates);
    }
  } catch {
    // Never let tracking errors surface to the user
  }
}

// ── Track a logged-in user page view (separate counter block) ─────────────────
// Nothing personally identifiable is stored in Firestore.

export async function trackUserPageView(pathname: string, userId: string): Promise<void> {
  if (typeof window === 'undefined') return;
  try {
    const sessionKey = `bb_user_session_${userId.slice(-6)}`;
    const isNewSession = !sessionStorage.getItem(sessionKey);
    if (isNewSession) sessionStorage.setItem(sessionKey, '1');

    const ref = doc(db, 'guestAnalytics', 'aggregate');
    const updates = {
      userTotalPageViews: increment(1),
      ...(isNewSession ? { userTotalSessions: increment(1) } : {}),
      ...buildDimensionUpdates(pathname, 'user'),
      updatedAt: new Date().toISOString(),
    };

    try {
      await updateDoc(ref, updates);
    } catch {
      await ensureAggregateDoc();
      await updateDoc(ref, updates);
    }
  } catch {
    // Never surface tracking errors
  }
}

// ── Fetch aggregate stats ─────────────────────────────────────────────────────

export async function getGuestAnalytics(): Promise<GuestAnalyticsAggregate | null> {
  // Server route first (Gordon 2026-11-01: the in-page read failed on the busy
  // Dashboard and showed "no data yet" over 9,000+ recorded visits), then the
  // direct read as a fallback.
  try {
    const res = await fetch('/api/analytics/visitors');
    if (res.ok) {
      const data = await res.json();
      if (data && typeof data === 'object' && !('error' in data)) return data as GuestAnalyticsAggregate;
    }
  } catch { /* fall back below */ }
  try {
    const snap = await getDoc(doc(db, 'guestAnalytics', 'aggregate'));
    if (!snap.exists()) return null;
    return snap.data() as GuestAnalyticsAggregate;
  } catch (err) {
    console.error('[guest-analytics] read failed', err);
    return null;
  }
}

// ── Seed the aggregate doc if it doesn't exist (call from admin dashboard) ────
// Safe to call any time — only creates if missing, never overwrites existing data.

export async function initGuestAnalyticsIfMissing(): Promise<GuestAnalyticsAggregate> {
  try {
    const ref = doc(db, 'guestAnalytics', 'aggregate');
    const snap = await getDoc(ref);
    if (snap.exists()) return snap.data() as GuestAnalyticsAggregate;
    await setDoc(ref, EMPTY_AGG);
    return EMPTY_AGG as unknown as GuestAnalyticsAggregate;
  } catch {
    return EMPTY_AGG as unknown as GuestAnalyticsAggregate;
  }
}

// ── Track a UI element click (nav items, buttons, sections) ──────────────────
// element: short label e.g. "nav:music", "btn:play", "card:artist"
// Call from onClick handlers on key interactive elements.

export async function trackClick(element: string, isLoggedIn = false): Promise<void> {
  if (typeof window === 'undefined') return;
  try {
    const safe = element.replace(/[^a-z0-9:_-]/gi, '_').slice(0, 64);
    const ref = doc(db, 'guestAnalytics', 'aggregate');
    const updates: Record<string, ReturnType<typeof increment> | string> = {
      [`clicks.${safe}`]: increment(1),
      ...(isLoggedIn ? { [`user.clicks.${safe}`]: increment(1) } : {}),
      updatedAt: new Date().toISOString(),
    };
    try {
      await updateDoc(ref, updates as any);
    } catch {
      await ensureAggregateDoc();
      await updateDoc(ref, updates as any);
    }
  } catch {
    // Never surface
  }
}

// ── Browser profile for the current visitor ───────────────────────────────────

export async function getBrowserProfile(): Promise<BrowserProfile> {
  const nav = typeof navigator !== 'undefined' ? navigator : null;
  const win = typeof window !== 'undefined' ? window : null;

  // WebGL renderer
  let webGLRenderer: string | null = null;
  let webGLVendor: string | null = null;
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl') as WebGLRenderingContext | null;
    if (gl) {
      const dbg = gl.getExtension('WEBGL_debug_renderer_info');
      if (dbg) {
        webGLRenderer = gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL) || null;
        webGLVendor   = gl.getParameter(dbg.UNMASKED_VENDOR_WEBGL) || null;
      }
    }
  } catch { /* non-fatal */ }

  // Battery
  let batteryLevel: number | null = null;
  let batteryCharging: boolean | null = null;
  try {
    if (nav && 'getBattery' in nav) {
      const bat = await (nav as any).getBattery();
      batteryLevel  = Math.round(bat.level * 100);
      batteryCharging = bat.charging;
    }
  } catch { /* non-fatal */ }

  // Network
  const conn = (nav as any)?.connection || (nav as any)?.mozConnection || (nav as any)?.webkitConnection;

  // Storage availability checks
  const storageAvailable = (type: 'localStorage' | 'sessionStorage') => {
    try { const s = win ? (win as any)[type] : null; s?.setItem('_t', '1'); s?.removeItem('_t'); return true; } catch { return false; }
  };

  return {
    browser: detectBrowser(),
    browserVersion: detectBrowserVersion(),
    os: detectOS(),
    osVersion: detectOSVersion(),
    device: detectDevice(),
    screenW: win?.screen.width ?? 0,
    screenH: win?.screen.height ?? 0,
    viewportW: win?.innerWidth ?? 0,
    viewportH: win?.innerHeight ?? 0,
    pixelRatio: win?.devicePixelRatio ?? 1,
    colorDepth: win?.screen.colorDepth ?? 24,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    language: nav?.language ?? 'unknown',
    languages: Array.from(nav?.languages ?? []),
    cookiesEnabled: nav?.cookieEnabled ?? false,
    doNotTrack: nav?.doNotTrack === '1' ? true : nav?.doNotTrack === '0' ? false : null,
    hardwareConcurrency: nav?.hardwareConcurrency ?? 1,
    deviceMemoryGb: (nav as any)?.deviceMemory ?? null,
    touchPoints: nav?.maxTouchPoints ?? 0,
    onLine: nav?.onLine ?? true,
    connectionType: conn?.effectiveType ?? null,
    downlinkMbps: conn?.downlink ?? null,
    rttMs: conn?.rtt ?? null,
    localStorageAvailable: storageAvailable('localStorage'),
    sessionStorageAvailable: storageAvailable('sessionStorage'),
    indexedDBAvailable: 'indexedDB' in (win ?? {}),
    serviceWorkerAvailable: 'serviceWorker' in (nav ?? {}),
    webAssemblyAvailable: typeof WebAssembly !== 'undefined',
    geolocationAvailable: 'geolocation' in (nav ?? {}),
    webShareAvailable: 'share' in (nav ?? {}),
    webGLRenderer,
    webGLVendor,
    batteryLevel,
    batteryCharging,
    referrer: typeof document !== 'undefined' ? document.referrer : '',
    plugins: nav?.plugins?.length ?? 0,
    // New fields
    colorScheme: detectColorScheme(),
    reducedMotion: detectReducedMotion(),
    highContrast: typeof window !== 'undefined' && window.matchMedia('(forced-colors: active)').matches,
    orientation: detectOrientation(),
    mediaCodecs: detectMediaCodecs(),
    canvasFingerprint: typeof document !== 'undefined' ? getCanvasFingerprint() : null,
    audioContext: typeof AudioContext !== 'undefined' ? getAudioContextFingerprint() : null,
  };
}
